import { Stepper } from "@shared/components/Stepper";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useBillDraftDetails, useConsumers, useBillTemplates } from "@features/bx/singleBillGenaration/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import Step1BasicInfo from "./Step1BasicInfo";
import Step2AccountMetersPlans from "./Step2AccountMetersPlans";
import Step3ReadingsAndPayments from "./Step3ReadingsAndPayments";
import { Step4Review } from "./Step4Review";
import { PageHeader } from "@shared/components/PageHeader";
import { formatTerritoryAddress } from "@shared/utils/address";

const DraftHydrator = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const draftIdFromUrl = new URLSearchParams(location.search).get("draftId");
  const remoteUtilityId = useRemoteUtilityId();

  const { data: draftDetails } = useBillDraftDetails(remoteUtilityId, draftIdFromUrl);
  const consumerNo = draftDetails?.result?.consumerNo;
  const { data: consumers } = useConsumers(consumerNo);
  const { data: billTemplates } = useBillTemplates();

  const [isHydrated, setIsHydrated] = useState(!draftIdFromUrl);

  const getAddressField = (address: any) => {
    if (address) {
      const serviceAddress = { "service": address };
      return formatTerritoryAddress(serviceAddress, "service");
    }
    return "";
  };

  useEffect(() => {
    if (!draftIdFromUrl) {
      setIsHydrated(true);
      return;
    }

    if (draftDetails && consumers && consumers.length > 0 && billTemplates && !isHydrated) {
      const targetConsumer = consumers[0];
      const billDraftData = draftDetails?.result?.billDraftData;
      
      const configId = billDraftData?.billConfigId;
      const matchedTemplate = billTemplates?.find((t: any) => t.value === configId);
      
      const step1Data = {
        selectedConsumer: targetConsumer,
        searchQuery: consumerNo,
        formData: {
          frequency: billDraftData?.frequency,
          startDate: billDraftData?.invoiceStartDate,
          endDate: billDraftData?.invoiceEndDate,
          billTemplate: matchedTemplate ? JSON.stringify(matchedTemplate) : null,
        },
        billTemplateData: matchedTemplate || null,
        billDraftId: draftIdFromUrl,
        consumerSelected: true,
        consumerData: {
          id: targetConsumer?.id,
          name: targetConsumer?.consumerName,
          accountNo: targetConsumer?.consumerNo,
          premiseId: targetConsumer.premiseCode,
          status: targetConsumer?.status,
          utilities: targetConsumer?.utilityService,
          totalOutstanding: targetConsumer?.billDetails?.billAmount,
          lastBillDate: targetConsumer?.billDetails?.billDate,
          activateDate: targetConsumer?.activateDate,
        },
        isStepComplete: true,
        timestamp: new Date().toISOString(),
        stepIndex: 0
      };

      const stepData: Record<number, any> = {
        0: step1Data,
      };

      const meterData = billDraftData?.readingData;
      if (meterData && Array.isArray(meterData)) {
        const transformedReadingData = meterData.map((reading: any) => ({
          meter_id: String(reading.meter),
          meter_number: reading.meterNumber,
          current_reading: Number(reading.currentReading),
          reading_date: reading.readingDate,
          reading_source: Number(reading.readingSource),
          status: String(reading.status),
          product_code: reading.productCode,
        }));

        stepData[2] = {
          reading_data: transformedReadingData,
          payment_settings: {
            outstanding: !billDraftData?.excludeOutstandingBalance,
            late_fees: billDraftData?.includeLateFees,
            credit_balance: billDraftData?.includeCredit,
            installments: billDraftData?.includeInstallments,
          },
        };
      }

      const hash = window.location.hash.replace("#", "");
      let currentStep = 0;
      if (hash === "account-overview") currentStep = 1;
      else if (hash === "readings-payments") currentStep = 2;
      else if (hash === "generate-bill") currentStep = 3;

      const completedSteps = [];
      for (let i = 0; i < currentStep; i++) {
        completedSteps.push(i);
      }

      const storageObj = {
        currentStep,
        completedSteps,
        skippedSteps: [],
        stepData,
        timestamp: Date.now(),
        version: "1.0.0",
      };

      localStorage.setItem("single-bill", JSON.stringify(storageObj));
      setIsHydrated(true);
    }
  }, [draftDetails, consumers, billTemplates, draftIdFromUrl, consumerNo, isHydrated]);

  if (draftIdFromUrl && !isHydrated) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
        <div className="text-gray-500">Restoring draft state...</div>
      </div>
    );
  }

  return <>{children}</>;
};
// import Step6ApproveBills from "./steps/ApproveBills/ApproveBills";
// import Step1BillingConfiguration from "./steps/BillCycle/BillCycle";
// import Step2FetchConsumerData from "./steps/ConsumerData/ConsumerData";
// import Step4GenerateBill from "./steps/GenerateBill/GenerateBill";
// import Step3FetchMeterReadings from "./steps/MeterReading/MeterReading";
// import Step5ReviewBills from "./steps/ReviewBills/ReviewBills";

const SingleBillCreatrDraft = () => {
  const [workflowData, setWorkflowData] = useState({});

  const handleComplete = (
    allStepData: any,
    completedSteps: number[],
    skippedSteps: number[]
  ) => {
    console.log("Workflow completed:", allStepData);
    // Handle final submission
  };

  const stepperSteps = [
    {
      id: "consumer-setup",
      description: "Consumer Selection",
      component: <Step1BasicInfo />,
      slug: "consumer-setup",
      infoDescription:
      "For a consumer's first bill, the activation date must fall between the invoice period start date and end date. If generating a single bill, the current invoice period must be after the previous one. Example: Previous: 01/01/2025–30/01/2025 → Current: 01/02/2025–29/02/2025. Select the consumer for whom you want to generate a bill. You can search by account number, name, or other identifiers."
    },
    {
      id: "account-overview",
      description: "Account Overview",
      component: <Step2AccountMetersPlans />,
      slug: "account-overview",
    },
    {
      id: "readings-payments",
      description: "Readings & Payments",
      component: <Step3ReadingsAndPayments />,
      slug: "readings-payments",
    },
    {
      id: "generate-bill",
      description: "Generate Bill",
      component: <Step4Review />,
      slug: "generate-bill",
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between p-6 border-b">
        <PageHeader title="Single Bill Generation" subtitle="Create and manage billing cycles using this streamlined workflow" />
      </div>

      {/* Stepper Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <DraftHydrator>
          <Stepper
            steps={stepperSteps}
            onComplete={handleComplete}
            autoSave={true}
            persistProgress={true}
            storageKey="single-bill"
            allowSkip={false}
            enableRouting={true}
            className="w-full"
            allowStepClick={true}
            enableDraftMode={true}
            showDraftResume={true}
            onStepChange={undefined}
            onValidation={undefined}
            onStepSave={undefined}
          />
        </DraftHydrator>
      </div>
    </div>
  );
};

export default SingleBillCreatrDraft;